<template>
	<div>
		<div v-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-if="!manualTypes.length && !loadingTypes" class="alert alert-warning">
			No deduction types are set up yet. Add one under Deduction types first.
		</div>

		<!-- Fixed when opened from a specific row; pickable when opened standalone. -->
		<p class="text-muted" v-if="!allowPickEmployee || !allowPickDate">
			<strong v-if="!allowPickEmployee && employeeName">{{ employeeName }}</strong>
			<span v-if="!allowPickEmployee && employeeName && !allowPickDate"> · </span>
			<strong v-if="!allowPickDate">{{ formatDate(form.work_date) }}</strong>
		</p>

		<div class="row" v-if="allowPickEmployee || allowPickDate">
			<div class="col-md-7 form-group" v-if="allowPickEmployee">
				<label>Employee</label>
				<select class="form-control form-cascade-control" v-model="form.employee_id">
					<option value="">Select an employee</option>
					<option v-for="person in employees" :key="person.id" :value="person.id">
						{{ person.name }}{{ person.employee_code ? ' · ' + person.employee_code : '' }}
					</option>
				</select>
			</div>
			<div class="col-md-5 form-group" v-if="allowPickDate">
				<label>Date</label>
				<input type="date" class="form-control form-cascade-control" v-model="form.work_date" />
			</div>
		</div>

		<div class="row">
			<div class="col-md-3 form-group">
				<label>{{ selectedType && selectedType.unit === 'days' ? 'Days' : 'Value' }}</label>
				<input
					type="number"
					min="0"
					step="0.01"
					class="form-control form-cascade-control"
					v-model="form.value"
				/>
			</div>
			<div class="col-md-9 form-group">
				<label>
					Remarks
					<small v-if="selectedType && selectedType.requires_note" class="text-danger">*</small>
				</label>
				<textarea
					rows="3"
					maxlength="500"
					class="form-control form-cascade-control"
					placeholder="Enter remarks"
					v-model="form.note"
				></textarea>
			</div>
		</div>

		<p class="text-muted" v-if="selectedType">
			<span v-if="selectedType.direction === 'credit'">Adds to</span>
			<span v-else>Deducts from</span>
			pay, measured in {{ unitWord(selectedType.unit) }}.
			<span v-if="selectedType.max_per_month">
				Capped at {{ Number(selectedType.max_per_month) }} per period.
			</span>
			<span v-if="selectedType.requires_approval">Held for approval before it counts.</span>
		</p>

		<div class="text-right" style="margin-top:12px">
			<button class="btn btn-default" @click="$emit('cancel')">Cancel</button>
			<button class="btn btn-primary" :disabled="saving || !manualTypes.length" @click="save">
				Save deduction
			</button>
		</div>
	</div>
</template>

<script>
import { deductionService, deductionTypeService, employeeService, errorMessage } from './attendanceService'
import attendanceFormat from './attendanceFormat'

/**
 * One deduction, for one employee, on one day.
 *
 * Shared by the daily register (where the person and date come from the row)
 * and the deductions page (where both are picked), so the rules about defaults,
 * required notes and caps are stated once.
 */
export default {
	name: 'AttendanceDeductionForm',
	mixins: [attendanceFormat],
	props: {
		employeeId: { type: [Number, String], default: '' },
		employeeName: { type: String, default: '' },
		workDate: { type: String, default: '' },
		allowPickEmployee: { type: Boolean, default: false },
		allowPickDate: { type: Boolean, default: false },
	},
	data() {
		return {
			types: [],
			employees: [],
			loadingTypes: false,
			saving: false,
			error: '',
			form: {
				employee_id: this.employeeId || '',
				work_date: this.workDate || this.toIsoDate(new Date()),
				deduction_type_id: '',
				value: '',
				note: '',
			},
		}
	},
	computed: {
		/** Types an admin may add by hand; policy-only ones are not offered. */
		manualTypes() {
			return this.types.filter((t) => t.is_active && t.allow_manual)
		},
		selectedType() {
			return this.types.find((t) => String(t.id) === String(this.form.deduction_type_id))
		},
	},
	watch: {
		employeeId(value) {
			this.form.employee_id = value || ''
		},
		workDate(value) {
			if (value) this.form.work_date = value
		},
	},
	mounted() {
		this.loadTypes()
		if (this.allowPickEmployee) this.loadEmployees()
	},
	methods: {
		loadTypes() {
			this.loadingTypes = true
			deductionTypeService
				.list()
				.then((data) => {
					this.types = data || []
					if (!this.form.deduction_type_id && this.manualTypes.length) {
						this.form.deduction_type_id = this.manualTypes[0].id
						this.syncDefaultValue()
					}
				})
				.catch(() => {
					this.types = []
				})
				.finally(() => {
					this.loadingTypes = false
				})
		},
		loadEmployees() {
			employeeService
				.list()
				.then((data) => {
					this.employees = (data || []).filter((person) => person.is_active !== false)
				})
				.catch(() => {})
		},
		unitWord(unit) {
			if (unit === 'days') return 'paid days'
			if (unit === 'hours') return 'hours'
			return 'money'
		},
		/** Prefill what the type suggests, without overwriting a typed value. */
		syncDefaultValue() {
			const type = this.selectedType
			if (type && (this.form.value === '' || this.form.value == null)) {
				this.form.value = type.default_value == null ? '' : type.default_value
			}
		},
		/** Clear back to an empty form so the modal can be reopened cleanly. */
		reset() {
			this.error = ''
			this.form = {
				employee_id: this.employeeId || '',
				work_date: this.workDate || this.toIsoDate(new Date()),
				deduction_type_id: this.manualTypes.length ? this.manualTypes[0].id : '',
				value: '',
				note: '',
			}
			this.syncDefaultValue()
		},
		save() {
			if (!this.form.employee_id || !this.form.work_date || !this.form.deduction_type_id) {
				this.error = 'Pick an employee and a date, and ensure a manual deduction type is configured.'
				return
			}
			this.saving = true
			this.error = ''
			deductionService
				.create({
					employee_id: this.form.employee_id,
					work_date: this.form.work_date,
					deduction_type_id: this.form.deduction_type_id,
					value: this.form.value,
					note: this.form.note || undefined,
				})
				.then((row) => {
					this.$emit('saved', row)
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not save that deduction.')
				})
				.finally(() => {
					this.saving = false
				})
		},
	},
}
</script>
